if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.bindDoodleGalleryControls();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    bindDoodleGalleryControls() {
      this.doodleDots = this.querySelector('.dd-gallery-mobile-dots');
      this.buildDoodleDots();
      this.bindDoodleMobileSwipe();
      this.querySelector('.dd-gallery-overlay-prev')?.addEventListener('click', () => this.stepDoodleGallery(-1));
      this.querySelector('.dd-gallery-overlay-next')?.addEventListener('click', () => this.stepDoodleGallery(1));
      this.elements.viewer.addEventListener('slideChanged', (event) => {
        if (event.detail?.currentElement) {
          this.updateDoodleGalleryControls(event.detail.currentElement.dataset.mediaId);
        }
      });
    }

    // On narrow screens, one horizontal gesture should navigate exactly one picture.
    // Allow normal vertical page scrolling and pinch gestures; don't change desktop.
    bindDoodleMobileSwipe() {
      const slider = this.elements.viewer?.querySelector('.product__media-list');
      if (!slider) return;
      const mobile = window.matchMedia('(max-width: 749px)');
      let swipe = null;

      slider.addEventListener('touchstart', (event) => {
        if (!mobile.matches || event.touches.length !== 1) {
          swipe = null;
          return;
        }
        const touch = event.touches[0];
        const items = this.doodleVisibleMedia();
        const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
        swipe = {
          x: touch.clientX,
          y: touch.clientY,
          index: Math.max(0, activeIndex),
          axis: null
        };
      }, { passive: true });

      slider.addEventListener('touchmove', (event) => {
        if (!swipe || !mobile.matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = touch.clientX - swipe.x;
        const dy = touch.clientY - swipe.y;
        if (!swipe.axis && (Math.abs(dx) > 9 || Math.abs(dy) > 9)) {
          swipe.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
        }
        // Stop native horizontal momentum, which can skip two or more slides.
        if (swipe.axis === 'horizontal' && event.cancelable) event.preventDefault();
      }, { passive: false });

      slider.addEventListener('touchend', (event) => {
        if (!swipe) return;
        const gesture = swipe;
        swipe = null;
        if (!mobile.matches || gesture.axis !== 'horizontal' || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - gesture.x;
        if (Math.abs(dx) < 28) return;
        const items = this.doodleVisibleMedia();
        // A full-width swipe always advances by one, even if it was very fast.
        const nextIndex = Math.max(0, Math.min(items.length - 1, gesture.index + (dx < 0 ? 1 : -1)));
        const target = items[nextIndex];
        if (target) this.setActiveMedia(target.dataset.mediaId, false);
      }, { passive: true });
      slider.addEventListener('touchcancel', () => { swipe = null; }, { passive: true });
    }

    doodleVisibleMedia() {
      return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'))
        .filter((media) => !media.classList.contains('product__media-item--variant') || media === media.parentElement.firstElementChild);
    }

    buildDoodleDots() {
      if (!this.doodleDots) return;
      this.doodleDots.replaceChildren();
      this.doodleVisibleMedia().forEach((media, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dd-gallery-dot';
        dot.dataset.target = media.dataset.mediaId;
        dot.setAttribute('aria-label', `View product image ${index + 1}`);
        dot.addEventListener('click', () => this.setActiveMedia(media.dataset.mediaId, false));
        this.doodleDots.appendChild(dot);
      });
      this.updateDoodleGalleryControls();
    }

    updateDoodleGalleryControls(mediaId) {
      const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
      const selected = mediaId || activeMedia?.dataset.mediaId;
      this.doodleDots?.querySelectorAll('.dd-gallery-dot').forEach((dot) => {
        const active = dot.dataset.target === selected;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    stepDoodleGallery(direction) {
      const items = this.doodleVisibleMedia();
      if (items.length < 2) return;
      const current = items.findIndex((item) => item.classList.contains('is-active'));
      const nextIndex = (Math.max(0, current) + direction + items.length) % items.length;
      this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
    }

    onSlideChanged(event) {
      const current = event.detail.currentElement;
      if (!current) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('is-active', element === current);
      });
      if (this.elements.thumbnails) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }
      this.updateDoodleGalleryControls();
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      // Keep the Shopify media and thumbnail order intact on variant changes.
      // Only the active image changes; no DOM nodes are moved.

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      this.updateDoodleGalleryControls();
      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
